/**
 * sw-update — les tablettes/caisses suivent les déploiements.
 *
 * Constat 2026-09-10 (SK FOOD, D3 PRO) : une caisse restée ouverte plusieurs
 * jours tournait sur un bundle d'août servi par le service worker — bugs
 * corrigés depuis (image plein écran, menus) toujours visibles. Le navigateur
 * ne vérifie le service worker qu'à la navigation ; une caisse ne navigue pas.
 *
 * Règles :
 *  - vérification du SW toutes les 10 min, au retour en ligne et quand l'app
 *    redevient visible ;
 *  - nouveau bundle prêt → rechargement UNIQUEMENT quand personne ne touche
 *    l'écran depuis 3 min et qu'aucun dialogue n'est ouvert (jamais au milieu
 *    d'un encaissement) ; sinon on réessaie chaque minute.
 */

import {
  IDLE_BEFORE_RELOAD_MS,
  RETRY_RELOAD_MS,
  SW_CHECK_INTERVAL_MS,
  canReloadNow,
  createInteractionTracker,
  type IdleProbe,
} from './sw-update-policy';

export { IDLE_BEFORE_RELOAD_MS, RETRY_RELOAD_MS, SW_CHECK_INTERVAL_MS, canReloadNow };

const hasOpenDialog = (doc: Document = document): boolean =>
  doc.querySelector(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
  ) !== null;

/**
 * Enregistre le service worker (mode « prompt » de vite-plugin-pwa) et pilote
 * la mise à jour. Retourne une fonction de nettoyage (tests / HMR).
 */
export async function installServiceWorkerUpdater(): Promise<() => void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const { registerSW } = await import('virtual:pwa-register');
  const tracker = createInteractionTracker();
  const timers: number[] = [];
  let pendingReload = false;

  const tryReload = (updateSW: (reload?: boolean) => Promise<void>) => {
    const probe: IdleProbe = {
      now: Date.now,
      lastInteractionAt: tracker.lastInteractionAt,
      hasOpenDialog: () => hasOpenDialog(),
    };
    if (canReloadNow(probe)) {
      void updateSW(true);
      return;
    }
    timers.push(window.setTimeout(() => tryReload(updateSW), RETRY_RELOAD_MS));
  };

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      if (pendingReload) return;
      pendingReload = true;
      tryReload(updateSW);
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => void registration.update().catch(() => {});
      timers.push(window.setInterval(check, SW_CHECK_INTERVAL_MS));
      window.addEventListener('online', check);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
    },
  });

  return () => timers.forEach((t) => window.clearTimeout(t));
}
